import { schema } from 'normalizr'

// Schemas
import { song as songSchema } from 'normalize'

// Types
import { Song, Track } from 'types'

export type RevenueReportingStatus = {
  reportingOpen: boolean
  reportingNeeded: boolean
  paymentNeeded: boolean
  dueDate: string
}

export type RevenueStream = {
  already_paid: boolean
  is_locked: boolean
  amount_converted: number
  amount: number
  currency: string
  derivative_amount_converted: number
  derivative_amount: number
  derivative_share: number
  description: string
  id: number
  recouped_amount_converted: number
  revenue_report: number
  royalty_amount_converted: number
  royalty_amount: number
  source: string
  status: 'paid' | 'unpaid'
}

export type RevenueOrderOrderItem = {
  period: string
  reporting_close: string
  reporting_open: string
  songs: {
    [songId: string]: {
      title: string
      streams: Array<RevenueStream>
    }
  }
}

export type RevenueOrder = {
  id: number
  due_date: string
  order_items: {
    [period: string]: RevenueOrderOrderItem
  }
  total_amount: number
  state: 'UNPAID' | 'WAITING' | 'PAID'
  currency: string
  payment_url: string
  vat_rate: number
  vat_amount: number
  total_amount_already_paid: number
}

export type RegistrationServiceSteps = Array<{
  id: number
  link_url: string
  name: string
  percentage_modifier: string
  ordering: number
  text: string
  steps: Array<{
    id: number
    ordering: number
    text: string
  }>
  type: 'release' | 'works'
  warning: string
}>

export type RegistrationService = {
  id: number
  name: string
  type: string
  steps: RegistrationServiceSteps
  link_url: string
  description: string
  warning: string
  percentage_modifier: string
}

export type RevenueReportingPeriod = {
  id: number
  name: string
  start_date: string
  end_date: string
  reporting_open: string
  reporting_close: string
}

export type UserSongRevenueReport = {
  id: number
  currency: string
  order: number
  paid: boolean
  period: RevenueReportingPeriod
  reported: boolean
  revenue_streams: Array<RevenueStream>
  total_amount_reported: number
  total_amount: number
  total_amount_already_paid: number
  user_song: number
}

export type UserSongSampleRuntime = {
  id: number
  description: string
  start_time: string
  end_time: string
}

export type UserSongSample = {
  id: number
  sample_runtimes: Array<UserSongSampleRuntime>
  license_sample_type: number
  license_order: number
  license_version_id: number | null | undefined
  total_runtime_seconds: number
  song: (Song & { tracks: Array<Track> }) | number
  created_at: string
  updated_at: string
  state: 'UNLICENSED' | 'LICENSED'
  payment_status: 'UNPAID' | 'WAITING' | 'PAID' | null
  recoupable_total: number
  recoupable_remaining: number
}

export type UserSongState = 'UNLICENSED' | 'INCOMPLETE' | 'ACTIVE' | 'INVALID' | 'TERMINATED'

export type UserSongRightsholder = {
  id: number
  name: string
  ipi_number: string | number
  share: number
  derivative_rightsholder: boolean
  affiliation: string
  type: 'PUBLISHER' | 'WRITER'
  connected_to_ipi?: number | string
  modified?: boolean
}

export type Beat = {
  id: number
  title: string | null
  audio_file: string | null
  audio_file_name: string | null
  visibility: 'private' | 'unlisted' | 'public'
  cover: string | null
  slug: string | null
  terms_accepted: boolean
  user: number
  user_song: number
  avatar: string
  alias: string
}

export type UserSong = {
  id: number
  artist_name: string
  audio_file_name: string
  beat: number
  created_at: string
  custom_release_registration_service: string
  isrc: string
  iswc: string
  license_activation_date: string
  license_purchase_date: string
  licensee: string
  recording_registered: boolean
  recording_registration_service: RegistrationService
  release_registration_service: RegistrationService
  revenue_reports: Array<number>
  rightsholders: Array<UserSongRightsholder>
  runtime: string
  samples: Array<UserSongSample>
  song_released: boolean
  song_title: string
  state: UserSongState
  status: string
  updated_at: string
  work_registered: boolean
  work_registration_service: RegistrationService
}

export type PRO = {
  name: string
  country: string
}

export const revenueOrderSchema = new schema.Entity('revenueOrders')
export const revenueOrderList = [revenueOrderSchema]
export const registrationServiceSchema = new schema.Entity('registrationServices')
export const registrationServiceListSchema = [registrationServiceSchema]
export const userSongRevenueReportSchema = new schema.Entity('userSongRevenueReports', {
  order: revenueOrderSchema,
})
export const userSongRevenueReportListSchema = [userSongRevenueReportSchema]
export const beatSchema = new schema.Entity('beats')
export const beatListSchema = [beatSchema]
export const userSongSchema = new schema.Entity('userSongs', {
  samples: [{ song: songSchema }],
  revenue_reports: [userSongRevenueReportSchema],
  beat: beatSchema,
})
export const userSongListSchema = [userSongSchema]
const proSchema = new schema.Entity('pros')
export const proListSchema = [proSchema]
export const revenueReportPeriodSchema = new schema.Entity('revenueReportPeriods')
export const revenueReportPeriodListSchema = [revenueReportPeriodSchema]
